/**
 * Embedded partner mode: Sarva shown inside a partner product (an iframe in their screen), or
 * opened from one by a partner launch (backend D148).
 *
 * Asked for three ways, any of which is enough: the app is in a frame; the first URL this page
 * loaded carried `?embed=1`; or the session came from a partner launch (`me.launch`, checked by
 * the layout). The `?embed=1` is read once, when the bundle loads, and kept **in memory** — the
 * sign-in redirect drops the query string, and storage is unreliable in a third-party frame.
 *
 * Auth is unchanged: the access token lives in memory. A launched session's refresh cookie is
 * **partitioned** (CHIPS, backend D269), so inside the partner's iframe the browser sends it and
 * the ordinary silent refresh keeps the session alive; a browser without partitioned cookies
 * (older Safari) still ends it with the access token's 30 minutes (D254), and the partner
 * launches again. Which origins may frame the app is set at build time (`SARVA_FRAME_ANCESTORS`,
 * see vite.config.ts) — not here: the browser enforces it, the app cannot.
 */
const requestedByUrl =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("embed") === "1";

function inFrame(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin parent makes `window.top` unreadable in some browsers: that is a frame.
    return true;
  }
}

export function embedRequested(): boolean {
  return typeof window !== "undefined" && (requestedByUrl || inFrame());
}

/** The message a partner page listens for when the user is finished in Sarva. Carries no data. */
export const EMBED_DONE_MESSAGE = { type: "sarva:done" } as const;

/**
 * "Done": tell the partner page (it closes the panel), or close a tab a partner opened.
 *
 * Returns `false` when it could not hand control back: a browser closes only a tab a script
 * opened with one history entry, so after the user has moved around in it `window.close()` is
 * silently ignored, and the caller then says "you can close this tab".
 */
export async function finishEmbedded(): Promise<boolean> {
  if (inFrame()) {
    // No PHI and no token in the message, so no target origin is needed to protect it.
    window.parent.postMessage(EMBED_DONE_MESSAGE, "*");
    return true;
  }
  window.close();
  await new Promise((resolve) => setTimeout(resolve, 300));
  return window.closed;
}
